<template>
  <div>
    <div v-if="isFormVisible" class="overlay" @click="closeForm"></div>
    <div class="ellipsis" :class="{ selected: isFormVisible }">
      <EllipsisIcon @click="toggleForm" />
      <div v-if="isFormVisible" class="form-container">
        <span class="form-title">Export to Pdf </span>
        <div class="form-item" v-for="(value, key) in visibleOptions" :key="key">
          <label>
            {{ getDisplayName(key) }}
            <input
              type="checkbox"
              :value="value"
              @change="toggleExportOption(key)"
              :checked="value"
            />
          </label>
        </div>
        <button @click="exportSelectedViews" class="button">Export Selected Views</button>
        <button v-if="!axeOnlyMode" @click="exportCurrentView" class="button">Export Current View</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { EllipsisIcon } from '../icons'
import { exportComponentToPdf } from '../export/export'
import { useRoute } from 'vue-router';
import { RoleService } from '@/services/RoleService'

const route = useRoute();
const roleService = new RoleService()

const isFormVisible = ref(false)

function toggleForm() {
  isFormVisible.value = !isFormVisible.value
}

function closeForm() {
  isFormVisible.value = false
}

const exportOptions = ref({
  dbgAbout: true,
  dbgNetValue: true,
  arrotexValue: true,
  vidacorpValue: true,
  axeValue: true
})

const axeOnlyMode = computed(() => {
  const hasAxe = roleService.hasRole('axe')
  const hasArrotex = roleService.hasRole('arrotex')
  const hasVidacorp = roleService.hasRole('vidacorp')
  return hasAxe && !hasArrotex && !hasVidacorp
})

if (axeOnlyMode.value) {
  exportOptions.value = {
    dbgAbout: false,
    dbgNetValue: false,
    arrotexValue: false,
    vidacorpValue: false,
    axeValue: true
  }
}

type ExportOptionKey = keyof typeof exportOptions.value

const toggleExportOption = (key: ExportOptionKey) => {
  if (axeOnlyMode.value) {
    return
  }
  exportOptions.value[key] = !exportOptions.value[key]
}

const exportSelectedViews = () => {
  exportComponentToPdf('Dbg Hub', exportOptions.value)
}

const exportCurrentView = () => {
  for (const key in exportOptions.value) {
    exportOptions.value[key as ExportOptionKey] = false;
  }

  const routeToExportOptionMap: Record<string, ExportOptionKey> = {
    '/about': 'dbgAbout',
    '/benefits': 'dbgNetValue',
    '/benefits/arrotex': 'arrotexValue',
    '/benefits/vidacorp': 'vidacorpValue',
    '/benefits/axe': 'axeValue'
  };

  const currentRouteKey = routeToExportOptionMap[route.path];

  if (currentRouteKey) {
    exportOptions.value[currentRouteKey] = true;
  }

  exportComponentToPdf('Dbg Hub', exportOptions.value);
};

const getDisplayName = (key: string) => {
  const displayNames: Record<string, string> = {
    dbgAbout: 'About DBG',
    dbgNetValue: 'DBG Net Value',
    arrotexValue: 'Arrotex Value',
    vidacorpValue: 'Vidacorp Value',
    axeValue: 'Axe Value',
  }
  return displayNames[key] || key
}

const visibleOptions = computed(() => {
  if (axeOnlyMode.value) return { axeValue: exportOptions.value.axeValue }
  return exportOptions.value
})
</script>

<style scoped>
.ellipsis {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  position: relative;
  cursor: pointer;
  color: var(--text-color);
  font-size: var(--font-size-menu-icon);
}

.ellipsis:hover {
  opacity: 0.5;
}

.ellipsis.selected {
  opacity: 1;
  z-index: 100;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-background-80);
  backdrop-filter: blur(2.5px);
  z-index: 99;
}

.form-container {
  position: absolute;
  top: 3.125rem;
  right: 0;
  background-color: var(--color-background-bright);
  padding: 1rem;
  border-radius: 0.9375rem;
  z-index: 1000;
  border: 0.0625rem solid var(--color-background);
  height: fit-content;
  width: 15.625rem;
  gap: 0.9375rem;
  display: flex;
  flex-direction: column;
}

.form-title {
  font-size: var(--font-size-h1);
  font-weight: 600;
  text-align: center;
}

.form-item {
  display: flex;
  gap: 0.3125rem;
  font-size: var(--font-size-small);
  font-style: italic;
  justify-content: space-between;
  height: 100%;
  align-items: center;
}

.form-item > label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.15625rem;
  cursor: pointer;
  width: 100%;
}

.form-container .button {
  width: 100%;
  height: 3.125rem;
  font-size: var(--font-size-body);
  font-weight: 600;
  background-color: var(--color-secondary);
  border-radius: 15.625rem;
  padding-inline: 0.625rem;
  color: var(--color-background-bright);
  transition:
    background-color 0.25s ease,
    color 0.25s ease;
}

.form-container .button:nth-of-type(1) {
  background-color: transparent;
  border: 0.0625rem solid var(--color-secondary);
  color: var(--text-color);
}

.form-container .button:nth-of-type(1):hover {
  opacity: 0.5;
}

.form-container .button:nth-of-type(2):hover {
  opacity: 0.5;
}
</style>
